import Input from "../../components/Input/Input"
import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import Button from "../../components/Button/Button"
import { NavLink, useNavigate } from "react-router-dom"
import { areFieldsNotEmpty } from "../../helpers/helper"
import { phoneNumber } from "../../utils/payment"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import Calendar from "../../components/Calendar/Calendar"
import { serverToLocalDateOnly } from "../../utils/date"
import { getAffiliateGroupsAdminById, updateAffiliateGroupsAdmin } from "../../features/affiliateGroupsAdmins/affiliateGroupsAdmins"
import { clearListCache } from "../../utils/listCache"
import Dropdown from "../../components/Dropdown/Dropdown"
import { fetchContactGroupsDropdownInfo } from "../../features/contactGroups/contactGroups"
import BrandAccessPanel from "../../components/BrandAccess/BrandAccessPanel"
import EmailSettingsPanel from "../../components/EmailSettings/EmailSettingsPanel"

const EditAffiliateGroupsAdmin = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userId = parts[parts.length - 1]

  const [reqError, setReqError] = useState('')
  const [contactGroups, setContactGroups] = useState([])
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    visibleGroups: []
  })
  const [isFormValid, setIsFormValid] = useState(false)
  const [homeCompany, setHomeCompany] = useState('')

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const handleEditAffiliateGroupsAdmin = async () => {
    let newNumber = formData?.primaryPhone
    triggerLoading(true)
    if (newNumber) {
      newNumber = formData.primaryPhone?.replace(/\D/g, "")
    }

    const response = await dispatch(updateAffiliateGroupsAdmin({
      userData: { ...formData, primaryPhone: newNumber },
      userId
    }))
    if (response.error) {
      setReqError(response.payload)
    } else if (response) {
      showSnackBar("success", "Your changes are saved")
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        visibleGroups: []
      })
      clearListCache('groupAdmins')
      navigate('/affiliate-groups-admins')
    }
    triggerLoading(false)
  }

  const getContactGroupsDropdownInfo = async () => {
    triggerLoading(true)
    const response = await dispatch(fetchContactGroupsDropdownInfo())
    if (response.error) {
      showSnackBar("error", response?.payload || "Something went wrong")
    } else {
      setContactGroups(response?.payload?.data)
    }
    triggerLoading(false)
  }

  const handleInputChange = ({ name, value }) => {
    switch (name) {
      case 'primaryPhone':
        setFormData((prevData) => ({ ...prevData, [name]: phoneNumber(value) }))
        break
      default:
        const [parentField, childField] = name.split('.')
        if (childField) {
          setFormData((prevData) => ({
            ...prevData,
            [parentField]: {
              ...prevData[parentField],
              [childField]: value
            }
          }))
        } else {
          setFormData((prevData) => ({
            ...prevData,
            [name]: value
          }))
        }
        break
    }
  }

  const getAffiliateGroupsAdminByIdReq = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getAffiliateGroupsAdminById(userId))
      setHomeCompany(response?.payload?.data?.company || '')
      const adminData = response?.payload?.data
      setFormData({
        firstName: adminData?.firstName || '',
        lastName: adminData?.lastName || '',
        email: adminData?.email || '',
        primaryPhone: phoneNumber(adminData?.primaryPhone) || '',
        address: {
          address: adminData?.address?.address || '',
          aptNumber: adminData?.address?.aptNumber || '',
          city: adminData?.address?.city || '',
          state: adminData?.address?.state || '',
          zip: adminData?.address?.zip || '',
        },
        dateOfBirth: serverToLocalDateOnly(adminData?.dateOfBirth) || '',
        visibleGroups: adminData?.visibleGroups?.map(g => g._id || g) || []
      })
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get affiliate groups admin data')
    }
    triggerLoading(false)
  }

  useEffect(() => {
    const checkFormValidity = () => {
      setIsFormValid(
        areFieldsNotEmpty([formData.firstName, formData.lastName, formData.email]) &&
        formData.dateOfBirth !== ""
      )
    }

    checkFormValidity()
  }, [formData])

  useEffect(() => {
    getAffiliateGroupsAdminByIdReq()
    getContactGroupsDropdownInfo()
  }, [])

  return (
    <ContainerPage>
      <div className="flex justify-between items-center">
        <PageTitle title="Edit group admin" backTo="/affiliate-groups-admins"
          subtitle={[formData?.firstName, formData?.lastName].filter(Boolean).join(' ')} />
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px] rounded-2xl bg-surface border border-divider shadow-card p-12">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider ">
            <Input
              width="w-full"
              placeholder="Enter First Name"
              label="First name *"
              value={formData.firstName}
              onChange={(e) => handleInputChange({ name: "firstName", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Last Name"
              label="Last name *"
              value={formData.lastName}
              onChange={(e) => handleInputChange({ name: "lastName", value: e.target.value })}
            />
            <Input
              type="email"
              width="w-full"
              placeholder="Enter Email"
              label="Email *"
              value={formData.email}
              onChange={(e) => handleInputChange({ name: "email", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Primary Phone"
              label="Primary Phone"
              value={formData.primaryPhone}
              onChange={(e) => handleInputChange({ name: "primaryPhone", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Address"
              label="Address"
              value={formData?.address?.address}
              onChange={(e) => handleInputChange({ name: "address.address", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="34"
              label="Suite/Apt#"
              value={formData?.address?.aptNumber}
              onChange={(e) => handleInputChange({ name: "address.aptNumber", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter City"
              label="City"
              value={formData?.address?.city}
              onChange={(e) => handleInputChange({ name: "address.city", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter State"
              label="State"
              value={formData?.address?.state}
              onChange={(e) => handleInputChange({ name: "address.state", value: e.target.value })}
            />
            <Input
              type="number"
              width="w-full"
              placeholder="Enter Zip"
              label="Zip"
              value={formData?.address?.zip}
              onChange={(e) => handleInputChange({ name: "address.zip", value: e.target.value })}
            />
            <Calendar
              width="w-full"
              label="Date Of Birth *"
              height="56px"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange({ name: "dateOfBirth", value: e })}
            />
            <div className="col-span-2">
              <Dropdown
                isMulti={true}
                value={formData?.visibleGroups}
                setValue={(e) => handleInputChange({ name: "visibleGroups", value: e })}
                menuItems={contactGroups}
                className="w-full mt-2"
                variant="standard"
                placeholder="Select groups"
                label="Visible Contact Groups"
              />
            </div>
          </div>
          <div className={`mt-6 flex justify-between items-center`}>
            <Button
              onClick={() => {
                const email = encodeURIComponent(formData.email || '')
                navigate(`/admin/reset-password?userId=${userId}&userType=affiliateGroupsAdmin&email=${email}`)
              }}
              type="outlined"
              size="lg"
              width="min-w-[156px]"
            >
              Reset Password
            </Button>
            <div className="flex space-x-4">
              <NavLink to={"/affiliate-groups-admins"}>
                <Button classes={"mr-4"} onClick={() => {
                }} type="outlined" size="lg" width="min-w-[156px]">Close</Button>
              </NavLink>
              <Button
                onClick={handleEditAffiliateGroupsAdmin}
                size="lg"
                width="min-w-[156px]"
                disabled={!isFormValid}
                classes="rounded-lg"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px]">
          <BrandAccessPanel userId={userId} homeCompany={homeCompany} />
          <EmailSettingsPanel userId={userId} />
        </div>
      </div>
    </ContainerPage>
  )
}

export default EditAffiliateGroupsAdmin
